import { ReactElement, useEffect, useMemo } from 'react'
import { MeshBasicMaterial, Quaternion, Vector3 } from 'three'
import { BatchedText, Text } from 'troika-three-text'
import {
  LABEL_FONT_URL,
  LABEL_LINE_HEIGHT,
  LABEL_OCCLUSION_ALPHA_TEST,
  LABEL_SDF_GLYPH_SIZE,
  LABEL_TEXT_RENDER_ORDER,
} from './constants'

export interface BatchedLabelTextItem {
  id: string
  /** lines separated by `\n` */
  text: string
  fontSize: number
  /** line height in em; defaults to `LABEL_LINE_HEIGHT` */
  lineHeight?: number
  color: string
  fillOpacity: number
  position: Vector3
  quaternion: Quaternion
}

/** Size of a rendered text block in world units, measured by troika */
export interface BatchedLabelTextSize {
  id: string
  width: number
  height: number
  /**
   * Offset of the visual center of the text from the center of its block, along
   * the text's own y axis.
   *
   * `anchorY: 'middle'` centers the line box, which reserves descender space
   * below the baseline, so the glyphs sit above the block center and a label
   * looks like it has too much room underneath. This is measured from the font
   * metrics rather than the drawn glyphs, so that a label containing a comma is
   * not shifted relative to one without.
   */
  centerOffsetY: number
}

interface Props {
  items: BatchedLabelTextItem[]
  /**
   * Let labels occlude each other instead of blending. Needs `alphaTest` so the
   * transparent parts of each glyph quad do not punch holes into whatever is
   * drawn after them, which in turn rules it out for translucent labels.
   */
  occlude?: boolean
  /**
   * Called with the measured block size of every item once troika has typeset
   * them. Must be memoized - it is a dependency of the batch rebuild.
   */
  onMeasured?: (sizes: BatchedLabelTextSize[]) => void
}

/**
 * Renders any number of text labels in a single draw call using troika's
 * (experimental) `BatchedText`. Each label keeps its own transform, color and
 * opacity; only the material is shared.
 */
const BatchedLabelText = ({ items, occlude = false, onMeasured }: Props): ReactElement => {
  const batch = useMemo(() => {
    const batchedText = new BatchedText()
    // We draw borders with a batched line mesh instead, and troika's outline
    // pass would cost a second draw call for the whole batch.
    batchedText.hasOutline = () => false
    batchedText.material = new MeshBasicMaterial({
      transparent: true,
      depthWrite: occlude,
      // discard the transparent parts of the glyph quads, otherwise they would
      // write depth and cut rectangular holes around every glyph
      alphaTest: occlude ? LABEL_OCCLUSION_ALPHA_TEST : 0,
      toneMapped: false,
    })
    batchedText.renderOrder = LABEL_TEXT_RENDER_ORDER
    // While the batch is being rebuilt (members swapped, typesetting still in
    // the worker) troika computes its bounds from the not yet typeset members,
    // which come out empty - and an empty bounding sphere gets the whole batch
    // frustum culled until the repack lands. The previous glyphs stay in the
    // geometry meanwhile, so skipping the culling keeps every label on screen
    // across a rebuild instead of blinking it off for a few frames.
    batchedText.frustumCulled = false

    return batchedText
  }, [occlude])

  useEffect(() => () => batch.dispose(), [batch])

  useEffect(() => {
    const texts = items.map(item => {
      const text = new Text()
      text.text = item.text
      text.font = LABEL_FONT_URL
      text.fontSize = item.fontSize
      text.lineHeight = item.lineHeight ?? LABEL_LINE_HEIGHT
      text.textAlign = 'center'
      text.anchorX = 'center'
      text.anchorY = 'middle'
      text.sdfGlyphSize = LABEL_SDF_GLYPH_SIZE
      text.color = item.color
      text.fillOpacity = item.fillOpacity
      text.position.copy(item.position)
      text.quaternion.copy(item.quaternion)
      text.updateMatrix()

      batch.addText(text)

      return text
    })

    batch.sync(() => {
      onMeasured?.(
        items.map((item, index) => {
          const renderInfo = texts[index].textRenderInfo
          const bounds = renderInfo?.blockBounds

          // The text reads as centered on its cap band: from the last baseline
          // up to the top of the tallest glyph on the first line. Taken from the
          // baseline rather than the ink bottom, so a comma's descender does not
          // shift a label against its neighbours.
          let centerOffsetY = 0
          if (renderInfo) {
            const lineCount = item.text.split('\n').length
            const lineHeight = item.lineHeight ?? LABEL_LINE_HEIGHT
            const lastBaseline =
              renderInfo.topBaseline - (lineCount - 1) * lineHeight * item.fontSize
            centerOffsetY = (lastBaseline + renderInfo.visibleBounds[3]) / 2
          }

          return {
            id: item.id,
            width: bounds ? bounds[2] - bounds[0] : 0,
            height: bounds ? bounds[3] - bounds[1] : 0,
            centerOffsetY,
          }
        }),
      )
    })

    return () => {
      texts.forEach(text => {
        batch.removeText(text)
        text.dispose()
      })
    }
  }, [items, batch, onMeasured])

  return <primitive object={batch} />
}

export default BatchedLabelText
